import {
  Card,
  Grid,
  Layout,
  Breadcrumbs,
  Heading,
  Form,
  Input,
  Spinner,
  Anchor,
  ExpandableSection,
  Modal,
} from '@enterprise-ui/canvas-ui-react';
import { getMap, getMapExt } from '../../app/neptune/neptuneSlice';
import { Link as ReactLink } from 'react-router-dom';
import { Button, SelectInput } from 'ui-library';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { SurveyMap } from '../../components/SurveyMap';
import { anyObstruction, VestibuleBlockers, VestibuleType } from './options';
import { logEvent, LogLevel } from '@praxis/component-logging';
import './styles/styles.scss';

import { useInstallerAuthContext } from '../../hooks/useInstallerAuthContext';
import { HeightFormField } from '../../components/HeightFormField';
import { PhotoUploader } from '../../components/PhotoUploader';
import {
  convertPhotosIntoFiles,
  obstructionConditional,
  vestibuleImageSelector,
} from '../../utils/surveyHelpers';
import { useSurveyDispatch } from '../../hooks/useSurveyDispatch';
import { convertFeetNumToDecimal } from '../../utils/helpers';

export const VestibuleTemplate = () => {
  /* Auth related constants */
  const { user } = useInstallerAuthContext();
  const dispatch = useDispatch();
  /* Navigation related constants */
  const location = useLocation()?.state;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vestibule_number = searchParams.get('vestibule');
  const floor_number = searchParams.get('floor');
  const [isPhotoMinHit, setIsPhotoMinHit] = useState(false);
  let [submitErr, setSubmitErr] = useState(false);
  let [isPhotoMaxHit, setIsPhotoMaxHit] = useState(false);
  const [isUpdateValid, setIsUpdateValid] = useState(false);
  /* Location State related data*/
  let { vestibuleValue, vestibuleLabel, coordinates, totalReaders } =
    location || {};
  let prevData = JSON?.parse(sessionStorage?.getItem(vestibuleValue));
  const store = location?.formData?.location_id;
  const [photoUploaded, setPhotoUploaded] = useState([]);
  const isPhotoArrEmpty = Boolean(
    photoUploaded.length && photoUploaded.every(Boolean),
  );
  const [formData, setFormData] = useState({
    ...location?.formData,
    ...prevData,
  });
  const { dispatchEndpoint } = useSurveyDispatch(
    formData,
    photoUploaded,
    setIsPhotoMinHit,
  );
  const response = useSelector((state) => state.neptune);
  const { error, loading } = useSelector((state) => state.vestibuleForm);
  const handleSubmit = async () => {
    try {
      setSubmitErr(false);
      await convertPhotosIntoFiles(photoUploaded, formData);
      await dispatchEndpoint();
      navigate(`/survey-success`, {
        state: {
          formData,
          vestibuleValue,
          vestibuleLabel,
          totalReaders,
        },
      });
    } catch (err) {
      logEvent({ message: err }, { level: LogLevel.Error });
      setSubmitErr(true);
    }
  };
  const canSubmit =
    [
      isUpdateValid,
      isPhotoArrEmpty,
      formData?.vestibule_type,
      formData?.floor_id,
      formData?.ceiling_height,
      formData?.is_obstructed_label,
      formData?.entrance_door_width,
      formData?.entrance_door_count,
      formData?.exit_door_count,
      formData?.exit_door_width,
    ].every(Boolean) && obstructionConditional(formData);

  useEffect(
    function dispatchMapRequest() {
      if (store) {
        let storeId = store.substr(1);
        let floorNum = floor_number;
        if (user) {
          let { token } = user;
          dispatch(getMapExt({ storeId, floorNum, token }));
        } else {
          dispatch(getMap({ storeId, floorNum }));
        }
      }
    },
    [dispatch, store, floor_number, user],
  );

  //timeout after 20 secs
  useEffect(() => {
    if (!loading) return;

    const timer = setTimeout(() => {
      setSubmitErr(true);
    }, 20000);

    return () => clearTimeout(timer);
  }, [loading]);

  return (
    <>
      <Layout.Body data-testid="vestibuleFormPage" includeRail>
        <Card className="hc-pa-normal">
          <Grid.Container>
            <Breadcrumbs className="breadcrumbSpacing">
              <Breadcrumbs.Item as={ReactLink} to="/">
                Home
              </Breadcrumbs.Item>
              <Breadcrumbs.Item as={ReactLink} to="/survey">
                Pre-Install Survey
              </Breadcrumbs.Item>
            </Breadcrumbs>
            <Grid.Item xs={12}>
              <Heading size={3}>
                {' '}
                Vestibule {vestibule_number} | Floor {floor_number} |{' '}
                {formData?.device_type}
              </Heading>
            </Grid.Item>
            {response?.error && (
              <Grid.Item xs={12}>
                <Card elevation={0}>
                  <Input.Info error>Map is currently unavailable.</Input.Info>
                </Card>
              </Grid.Item>
            )}
            {!response?.error && !response?.loading && coordinates && (
              <Grid.Item xs={12}>
                <SurveyMap
                  storeId={store}
                  floorNum={floor_number}
                  coordinates={coordinates}
                />
              </Grid.Item>
            )}
            {response?.loading && !response?.error && <Spinner />}
            {location?.formData && store ? (
              <Form
                role="form"
                id="registration_form"
                className="hc-mt-md"
                onSubmit={(e) => {
                  e.preventDefault();
                  sessionStorage.setItem(
                    vestibuleValue,
                    JSON?.stringify(formData),
                  );
                  handleSubmit();
                }}
              >
                <Grid.Container className="hc-pa-normal" align="center">
                  <Grid.Item xs={12}>
                    <SelectInput
                      data-testid="vestibule_type"
                      id="vestibule_type"
                      defaultValue={{
                        value: formData?.vestibule_type,
                        label: formData?.vestibule_type_label,
                      }}
                      classNamePrefix="customSelect"
                      label="Vestibule Type"
                      name="vestibuleType"
                      required
                      options={VestibuleType()}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          vestibule_type: e?.value,
                          vestibule_type_label: e?.label,
                          floor_id: floor_number,
                        });
                      }}
                    />
                    {formData?.vestibule_type !== 'Type F' &&
                      formData?.vestibule_type !== undefined && (
                        <ExpandableSection className="hc-bg-grey06">
                          <p className="hc-pt-none">
                            Tap here to view example image
                          </p>
                          <ExpandableSection.Content className="hc-pa-none">
                            <img
                              width="100%"
                              height="100%"
                              src={
                                vestibuleImageSelector(
                                  formData?.vestibule_type,
                                )[0]
                              }
                              alt="first_image"
                            />
                            {(formData?.vestibule_type === 'Type A' ||
                              formData?.vestibule_type === 'Type B') && (
                              <img
                                width="100%"
                                height="100%"
                                src={
                                  vestibuleImageSelector(
                                    formData?.vestibule_type,
                                  )[1]
                                }
                                alt="second_image"
                              />
                            )}
                          </ExpandableSection.Content>
                        </ExpandableSection>
                      )}
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <Form.Field
                      data-testid="entrance_door_num"
                      type="radio"
                      id="entrance_door_num"
                      label="Number of Doors in Entrance Lane"
                      name="Number of Doors in Entrance Lane"
                      value={formData?.entrance_door_count?.toString() || ''}
                      options={[
                        {
                          value: '1', //Placeholder!
                          label: '1',
                          disabled: false,
                        },
                        {
                          value: '2',
                          label: '2',
                          disabled: false,
                        },
                        {
                          value: '3',
                          label: '3',
                          disabled: false,
                        },
                        {
                          value: '4',
                          label: '4',
                          disabled: false,
                        },
                      ]}
                      required
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          entrance_door_count: Number(e?.target?.value),
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <Form.Field
                      data-testid="exit_door_num"
                      id="exit_door_num"
                      type="radio"
                      label="Number of Doors in Exit Lane"
                      name="Number of Doors in Exit Lane"
                      value={formData?.exit_door_count?.toString() || ''}
                      options={[
                        {
                          value: '1',
                          label: '1',
                          disabled: false,
                        },
                        {
                          value: '2',
                          label: '2',
                          disabled: false,
                        },
                        {
                          value: '3',
                          label: '3',
                          disabled: false,
                        },
                        {
                          value: '4',
                          label: '4',
                          disabled: false,
                        },
                      ]}
                      required
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          exit_door_count: Number(e?.target?.value),
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <HeightFormField
                      label="Height of the Ceiling:"
                      isChangeValid={setIsUpdateValid}
                      defaultValue={convertFeetNumToDecimal(
                        formData?.ceiling_height,
                      )}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          ceiling_height: e,
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <HeightFormField
                      label="Width of the Entrance Lane:"
                      isChangeValid={setIsUpdateValid}
                      defaultValue={convertFeetNumToDecimal(
                        formData?.entrance_door_width,
                      )}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          entrance_door_width: e,
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <HeightFormField
                      label="Width of the Exit Lane:"
                      isChangeValid={setIsUpdateValid}
                      defaultValue={convertFeetNumToDecimal(
                        formData?.exit_door_width,
                      )}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          exit_door_width: e,
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <SelectInput
                      data-testid="is_obstructed"
                      id="is_obstructed"
                      required
                      label="Are there any obstructions to the install?"
                      classNamePrefix="customSelect"
                      errorText="You must select an option"
                      defaultValue={{
                        value: formData.is_obstructed,
                        label: formData.is_obstructed_label,
                      }}
                      options={anyObstruction()}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          is_obstructed: Boolean(e?.value),
                          is_obstructed_label: e?.label,
                        });
                      }}
                    />
                  </Grid.Item>
                  {formData?.is_obstructed_label === 'Yes' && (
                    <Grid.Item xs={12}>
                      <SelectInput
                        label="Select your reason for the obstruction:"
                        classNamePrefix="customSelect"
                        data-testid="obstruction_reason"
                        id="obstruction_reason"
                        required
                        defaultValue={{
                          value: formData?.obstruction_reason,
                          label: formData?.obstruction_reason,
                        }}
                        options={VestibuleBlockers()}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            obstruction_reason: e?.value,
                          });
                        }}
                      />
                    </Grid.Item>
                  )}
                  {formData?.obstruction_reason === 'Other' &&
                    formData?.is_obstructed_label === 'Yes' && (
                      <Grid.Item xs={12}>
                        <Form.Field
                          data-testid="obstruction_other_reason"
                          classNamePrefix="customSelect"
                          id="obstruction_other_reason"
                          required
                          value={formData?.obstruction_other_reason}
                          label="Please type reason for being unable to install."
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              obstruction_other_reason: e?.target?.value,
                            });
                          }}
                        />
                      </Grid.Item>
                    )}
                  <Grid.Item xs={12}>
                    <Form.Field
                      data-testid="additional_notes"
                      value={formData?.additional_notes}
                      id="additional_notes"
                      type="textarea"
                      label="Other details if applicable"
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          additional_notes: e?.target?.value || '',
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <PhotoUploader
                      files={photoUploaded}
                      onChange={setPhotoUploaded}
                      maximum={12}
                      minimum={2}
                      fileSizeLimit={5} //MB
                      isMaximumHit={isPhotoMaxHit}
                      isMinimumHit={isPhotoMinHit}
                      setMaxLimit={setIsPhotoMaxHit}
                      setMinLimit={setIsPhotoMinHit}
                    />
                  </Grid.Item>
                </Grid.Container>
                {loading && (
                  <Grid.Item xs={12} align="center" justify="center">
                    <Spinner />
                  </Grid.Item>
                )}
                <Grid.Item xs={12} align="center" justify="center">
                  <Button
                    className="hc-ma-normal"
                    label="Prev"
                    type="secondary"
                    name="prev_button"
                    id="prev_button"
                    onClick={() => {
                      sessionStorage.setItem(
                        vestibuleValue,
                        JSON?.stringify(formData),
                      );
                      navigate(`/survey-reader-list/?q=${store}`, {
                        state: { formData },
                      });
                    }}
                  />
                  <Button
                    className="hc-ma-normal"
                    label="Continue"
                    type="submit"
                    name="next_button"
                    id="submit"
                    disabled={!canSubmit}
                  />
                </Grid.Item>
                <Modal
                  isVisible={submitErr}
                  headingText={
                    error
                      ? 'Something went wrong...'
                      : 'This is taking longer than usual..'
                  }
                  onRefuse={() => setSubmitErr(false)}
                >
                  <Grid.Item xs={12} className="hc-pa-normal">
                    {error ? (
                      <>
                        <p className=" hc-mb-normal">
                          Failed to submit the survey form. Please try
                          resubmitting or or restarting your session.
                        </p>
                        <p className="hc-pb-expanded">
                          <em>{error}</em>
                        </p>
                      </>
                    ) : (
                      <>
                        <p className=" hc-mb-expanded">
                          This request is taking longer than expected and may
                          not complete -{' '}
                          <strong>large attachments can cause delays.</strong>
                        </p>
                        <p className=" hc-mb-expanded">
                          Please try again or upload smaller files.
                        </p>
                      </>
                    )}
                  </Grid.Item>
                </Modal>
              </Form>
            ) : (
              <Card elevation={0} className="hc-pa-normal cardContainer">
                <Input.Info error>
                  There was an issue loading the details for this location.
                  Please make sure{' '}
                  <Anchor to="/survey" as={ReactLink} className="iot-link">
                    previous information{' '}
                  </Anchor>{' '}
                  was submitted correctly.
                </Input.Info>
              </Card>
            )}
          </Grid.Container>
        </Card>
      </Layout.Body>
    </>
  );
};
